import { ActionIcon, Button, Flex, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import useUpsertDetectedEntity, {
  useUndoMutation,
} from 'src/data-capture/detected-entities/hooks/DetectionActionMutations';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import {
  EntityCategory,
  getLabelId,
  getPayloadForNewDetectionSelector,
  selectActionDescription,
  selectCanInsert,
  selectCanUpdate,
  selectEntityTypeForDetection,
  selectExistingColumnVisible,
  selectIsDiscarded,
  selectIsResolved,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import { useEntityLayout } from './EntityLayoutProvider';

type Props = {
  title: string;
  NewComponent: ReactNode;
  ExistingComponent: ReactNode;
  category: EntityCategory;
  detectionId: string;
  fieldName?: string;
  grouped?: boolean;
  isDisabled?: boolean;
};

export default function EntityCell({
  title,
  NewComponent,
  ExistingComponent,
  category,
  detectionId,
  fieldName,
  grouped = false,
  isDisabled = false,
}: Props) {
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detectionId, fieldName),
  );
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detectionId, fieldName),
  );
  const isActive = !isDiscarded && !isResolved;
  const entityType = useDetectedEntitiesStore(state =>
    selectEntityTypeForDetection(state, detectionId),
  );
  const showExisting = useDetectedEntitiesStore(state =>
    selectExistingColumnVisible(state, category, entityType),
  );
  const { titleWidth, registerTitleWidth } = useEntityLayout();
  const labelId = getLabelId(category, detectionId, fieldName);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (titleRef.current) {
      registerTitleWidth(titleRef.current.scrollWidth);
    }
  }, [title, registerTitleWidth]);

  return (
    <Flex
      align={grouped ? 'flex-start' : 'center'}
      gap="md"
      bg={isDisabled ? 'gray.1' : 'white'}
      style={{ borderRadius: 16 }}
      px="md"
      bd="1px solid gray.2"
      data-testid="entity-cell"
    >
      <Flex ref={titleRef} miw={titleWidth} style={{ flexShrink: 0 }} py={grouped ? 'xl' : 'sm'}>
        <Text
          fw={700}
          c="gray.10"
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
          opacity={isActive ? 1 : 0.5}
          data-testid="entity-title"
          id={labelId}
        >
          {title}
        </Text>
      </Flex>
      <Flex
        flex={showExisting ? 1 : 2}
        py={grouped ? 'xl' : 'sm'}
        opacity={isActive ? 1 : 0.5}
        data-testid="entity-new"
      >
        {NewComponent}
      </Flex>
      {showExisting && (
        <Flex
          flex={1}
          py={grouped ? 'xl' : 'sm'}
          opacity={isActive ? 1 : 0.5}
          data-testid="entity-existing"
        >
          {ExistingComponent}
        </Flex>
      )}
      {isDisabled ? (
        <Flex miw={180} />
      ) : (
        <EntityCellActions
          grouped={grouped}
          detectionId={detectionId}
          category={category}
          fieldName={fieldName}
        />
      )}
    </Flex>
  );
}

const EntityCellActions = memo(function EntityCellActions({
  grouped,
  detectionId,
  category,
  fieldName,
}: {
  grouped: boolean;
  detectionId: string;
  category: EntityCategory;
  fieldName?: string;
}) {
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detectionId, fieldName),
  );
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detectionId, fieldName),
  );
  const actionDescription = useDetectedEntitiesStore(state =>
    selectActionDescription(state, category, detectionId, fieldName),
  );
  const isActive = !isDiscarded && !isResolved;
  if (isActive) {
    return (
      <Flex
        gap="xs"
        align="center"
        justify="flex-end"
        py={grouped ? 'md' : undefined}
        miw={180}
        data-testid="actions"
      >
        <UpdateButton detectionId={detectionId} category={category} fieldName={fieldName} />
        <AddNewButton detectionId={detectionId} category={category} fieldName={fieldName} />
        <DeleteButton detectionId={detectionId} category={category} fieldName={fieldName} />
      </Flex>
    );
  }

  return (
    <Flex
      gap="xs"
      align="center"
      justify="flex-end"
      py={grouped ? 'md' : undefined}
      miw={180}
      data-testid="actions"
    >
      <Text size="sm" c="gray.10" data-testid="action-description">
        {actionDescription}
      </Text>
      <UndoButton
        detectionId={detectionId}
        fieldName={fieldName}
        isDiscarded={isDiscarded}
        isResolved={isResolved}
        category={category}
      />
    </Flex>
  );
});

function UndoButton({
  detectionId,
  fieldName,
  isDiscarded,
  category,
}: {
  detectionId: string;
  fieldName?: string;
  isDiscarded: boolean;
  isResolved: boolean;
  category: EntityCategory;
}) {
  const { clientId } = useCurrentClient();
  const { undoDetectionLastAction } = useUndoMutation();
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const [isLoading, setIsLoading] = useState(false);

  async function handleUndo() {
    setIsLoading(true);
    if (isDiscarded) {
      setDiscarded(false, category, detectionId, {
        fieldNames: fieldName ? [fieldName] : undefined,
      });
      setIsLoading(false);
      return;
    }

    await undoDetectionLastAction(clientId, category, detectionId, fieldName);
    setIsLoading(false);
  }

  return (
    <Button
      variant="text"
      c="gray.10"
      onClick={handleUndo}
      data-testid="undo-button"
      loading={isLoading}
    >
      Undo
    </Button>
  );
}

function UpdateButton({
  detectionId,
  category,
  fieldName,
}: {
  detectionId: string;
  category: EntityCategory;
  fieldName?: string;
}) {
  const { canUpdate, reason } = useDetectedEntitiesStore(state =>
    selectCanUpdate(state, category, detectionId, fieldName),
  );
  const { clientId } = useCurrentClient();
  const { upsertDetection, isLoading } = useUpsertDetectedEntity(category, detectionId, fieldName);

  if (category === 'new') {
    return null;
  }

  const text = 'Update';

  async function handleUpdate() {
    try {
      await upsertDetection(clientId, detectionId);
    } catch (error) {
      notifyAndLogError(
        'Failed to update detection',
        'Something went wrong while updating the detection. Please try again.',
        error,
        { clientId, detectionId, fieldName },
      );
    }
  }

  return (
    <Tooltip label={reason} disabled={canUpdate}>
      <Button
        variant="text"
        c="error.6"
        disabled={!canUpdate}
        onClick={handleUpdate}
        data-testid="update-button"
        loading={isLoading}
      >
        {text}
      </Button>
    </Tooltip>
  );
}

function AddNewButton({
  detectionId,
  category,
  fieldName,
}: {
  detectionId: string;
  category: EntityCategory;
  fieldName?: string;
}) {
  const { canInsert, reason } = useDetectedEntitiesStore(state =>
    selectCanInsert(state, category, detectionId, fieldName),
  );
  const { upsertDetection, insertDetectionEntity, isLoading } = useUpsertDetectedEntity(
    category,
    detectionId,
    fieldName,
  );
  const selectUpsertPayload = useDetectedEntitiesStore(state =>
    getPayloadForNewDetectionSelector(state),
  );
  const { clientId } = useCurrentClient();
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);

  if (category === 'info-update') {
    return null;
  }

  async function handleAddNew() {
    const upsertPayload = selectUpsertPayload(category, detectionId);
    if (!upsertPayload) {
      return;
    }
    try {
      if (fieldName) {
        await upsertDetection(clientId, detectionId);
      } else {
        await insertDetectionEntity(clientId, category, detectionId);
      }
    } catch (error) {
      notifications.show({
        title: 'Failed to add new item',
        message: 'Something when wrong while adding the new item. Please try again.',
        color: 'red',
      });
      Logger.error('Failed to add new item', error, { detectionId, fieldName });
      setResolved(false, category, detectionId, fieldName);
    }
  }

  return (
    <Tooltip label={reason} disabled={canInsert}>
      <Button
        variant="text"
        c="error.6"
        disabled={!canInsert}
        onClick={handleAddNew}
        data-testid="add-new-button"
        loading={isLoading}
      >
        Add as new
      </Button>
    </Tooltip>
  );
}

function DeleteButton({
  detectionId,
  fieldName,
  category,
}: {
  detectionId: string;
  fieldName?: string;
  category: EntityCategory;
}) {
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const handleDiscard = () => {
    setDiscarded(true, category, detectionId, { fieldNames: fieldName ? [fieldName] : undefined });
  };

  return (
    <ActionIcon variant="subtle" c="error.6" onClick={handleDiscard} data-testid="discard-button">
      <IconTrash size={16} />
    </ActionIcon>
  );
}
